import { useTranslation } from "src/i18next"
import React from "react"
import { MarketingPreferencesDataModel } from "src/utilities/httpClients/gatewayClient"
import { FormProvider, useForm } from "src/hooks/useForm"
import { Heading, Button } from "@puregym/ui"
import { Spacer } from "src/components/spacer"
import { Submit } from "src/components/buttons/submit"
import { CommunicationPreferences } from "src/features/communicationPreferences"
import { fullWidth } from "src/styles/helpers"
import { PrivacyNotice } from "src/components/privacyNotice"
import { useAbortController } from "src/hooks/useAbortController"
import { Action } from "src/features/subscriptionCentre"
import { ErrorAlert } from "src/components/alerts/errorAlert"
import { largeButtonCss } from "src/components/buttons/largeButton"
import { Dispatch } from "react-use-elmish"
import { RenderHtml } from "src/components/renderHtml"
import { useGateway } from "src/hooks/useGateway"
import { ShowOnFeatureSwitch } from "src/providers/featureSwitchProvider"
import { useSetErrorMessage } from "src/providers/errorProvider"

export const UpdatePreferencesForm: React.FC<{
  preferences: MarketingPreferencesDataModel
  dispatch: Dispatch<Action>
}> = ({ preferences, dispatch }) => {
  const { t } = useTranslation(["common", "marketingPreferences"])

  const { setErrorMessage } = useSetErrorMessage()

  const abortController = useAbortController()
  const client = useGateway()

  const request = async (
    form: MarketingPreferencesDataModel,
    token: AbortSignal,
  ) => {
    try {
      setErrorMessage()

      await client.updateMarketingPreferences(form, token)

      dispatch({ type: "marketing_preferences_updated" })
    } catch (err) {
      if (!abortController.signal.aborted) {
        dispatch({ type: "unknown_error", err })
        formMethods.setIsSubmitting(false)
      }
    }
  }

  const formMethods = useForm<MarketingPreferencesDataModel>({
    initiateState: () => preferences,
    request,
    t,
  })

  const unsetAll = async () => {
    const updated = {
      ...preferences,
      receiveEmailNewsletter: false,
      receiveMarketingSms: false,
      receiveMarketingSocialMedia: false,
      receiveMarketingSurveys: false,
    }
    formMethods.setIsSubmitting(true)
    await request(updated, abortController.signal)
    formMethods.setIsSubmitting(false)
  }

  return (
    <main id="main">
      <Heading as="h2">{t("marketingPreferences:updateTitle")}</Heading>
      <ErrorAlert />
      <RenderHtml html={t("marketingPreferences:updateText")} />
      <ShowOnFeatureSwitch featureSwitch="displayPrivacyNotice">
        <PrivacyNotice />
      </ShowOnFeatureSwitch>
      <FormProvider {...formMethods}>
        <Spacer mt={2} css={fullWidth}>
          <CommunicationPreferences />
        </Spacer>
        <Spacer mt={2} css={fullWidth}>
          <Submit
            isSubmitting={formMethods.isSubmitting}
            disabled={!formMethods.isDirty}
            id="submit"
            label={t("common:localizedStrings.labels.saveChanges")}
            css={largeButtonCss}
          />
          <Spacer ml={1}>
            <Button
              variant="link"
              onClick={() => unsetAll()}
              type="button"
              id="unsubscribe-all"
            >
              {t("marketingPreferences:unsubscribeAll")}
            </Button>
          </Spacer>
        </Spacer>
      </FormProvider>
    </main>
  )
}
